import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Select,
  Input,
  Modal,
  Form,
  Popconfirm
} from 'antd';
import {
  EditOutlined,
  ReloadOutlined,
  UserOutlined,
  DeleteOutlined,
  EyeOutlined,
  TeamOutlined,
  ApartmentOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import {
  fetchRbacUsers,
  fetchRbacRoles,
  updateUserRole,
  removeUserRole,
  getRbacUser
} from '../../redux/actions/rbacActions';

const { Option } = Select;
const { Search } = Input;

const UsersTab = () => {
  const dispatch = useDispatch();
  const { users, roles, usersLoading, currentUser } = useSelector(state => state.rbac);
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState(undefined);
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchRbacUsers());
    dispatch(fetchRbacRoles());
  }, [dispatch]);

  const handleEditRole = (user) => {
    setSelectedUser(user);
    form.setFieldsValue({
      role_id: user.role_id
    });
    setIsModalVisible(true);
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    dispatch(getRbacUser(user.id));
    setIsDetailsModalVisible(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalVisible(false);
    setSelectedUser(null);
  };

  const handleRemoveRole = (userId) => {
    dispatch(removeUserRole(userId));
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedUser(null);
    form.resetFields();
  };

  const handleSubmit = (values) => {
    if (selectedUser) {
      dispatch(updateUserRole(selectedUser.id, values.role_id));
      handleModalClose();
    }
  };

  const handleRefresh = () => {
    dispatch(fetchRbacUsers());
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchText.toLowerCase()) ||
      user.email.toLowerCase().includes(searchText.toLowerCase());
    const matchesRole = roleFilter === undefined || user.role_id === roleFilter;
    return matchesSearch && matchesRole;
  });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div className="user-role-avatar" style={{ width: 32, height: 32, fontSize: 14 }}>
            {name ? name.charAt(0).toUpperCase() : 'U'}
          </div>
          <strong>{name}</strong>
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role_name',
      key: 'role_name',
      width: 180,
      render: (roleName, record) => (
        roleName ? (
          <Tag color="blue">{roleName}</Tag>
        ) : (
          <Tag color="default">No Role</Tag>
        )
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_verified',
      key: 'is_verified',
      width: 120,
      render: (verified) => (
        <Tag color={verified ? 'success' : 'warning'}>
          {verified ? 'Verified' : 'Not Verified'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: '2-digit'
        });
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Edit Role">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditRole(record)}
              size="small"
            />
          </Tooltip>
          {record.role_id && (
            <Popconfirm
              title="Remove Role"
              description="Are you sure you want to remove this user's role?"
              onConfirm={() => handleRemoveRole(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Remove Role">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const usersWithRoles = users.filter(u => u.role_id).length;
  const verifiedUsers = users.filter(u => u.is_verified).length;

  return (
    <div style={{ padding: '1rem' }}>
      {/* Stats Cards */}
      <div className="rbac-stats-grid">
        <div className="rbac-stat-card">
          <div className="rbac-stat-icon"><TeamOutlined /></div>
          <div className="rbac-stat-content">
            <div className="rbac-stat-value">{users.length}</div>
            <div className="rbac-stat-label">Total Users</div>
          </div>
        </div>
        <div className="rbac-stat-card" style={{ borderLeftColor: '#007bff' }}>
          <div className="rbac-stat-icon" style={{ background: '#cfe2ff', color: '#084298' }}><ApartmentOutlined /></div>
          <div className="rbac-stat-content">
            <div className="rbac-stat-value">{usersWithRoles}</div>
            <div className="rbac-stat-label">With Roles</div>
          </div>
        </div>
        <div className="rbac-stat-card" style={{ borderLeftColor: '#28a745' }}>
          <div className="rbac-stat-icon" style={{ background: '#d4edda', color: '#155724' }}><CheckOutlined /></div>
          <div className="rbac-stat-content">
            <div className="rbac-stat-value">{verifiedUsers}</div>
            <div className="rbac-stat-label">Verified</div>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="modern-table-container" style={{ marginTop: '1.5rem' }}>
        <div className="table-header">
          <h2 className="table-title">User Role Management</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={handleRefresh}>
              <ReloadOutlined /> Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px', marginBottom: '1rem' }}>
          <Space direction="horizontal" style={{ width: '100%', flexWrap: 'wrap' }}>
            <Search
              placeholder="Search by name or email"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: 300 }}
            />
            <Select
              placeholder="Filter by Role"
              value={roleFilter}
              onChange={setRoleFilter}
              allowClear
              style={{ width: 200 }}
            >
              {roles.map(role => (
                <Option key={role.id} value={role.id}>{role.role_name}</Option>
              ))}
            </Select>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          loading={usersLoading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} users`,
          }}
          scroll={{ x: 1100 }}
          locale={{
            emptyText: (
              <div className="empty-state">
                <div className="empty-state-icon"><TeamOutlined /></div>
                <div className="empty-state-text">No users found</div>
              </div>
            ),
          }}
        />
      </div>

      {/* User Details Modal */}
      <Modal
        open={isDetailsModalVisible}
        onCancel={handleCloseDetailsModal}
        footer={[
          <Button key="close" type="primary" onClick={handleCloseDetailsModal}>
            Close
          </Button>
        ]}
        width={600}
        destroyOnClose
        centered
        title={`User Details: ${selectedUser?.name || ''}`}
      >
        {usersLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading user details...</p>
          </div>
        ) : currentUser ? (
          <div style={{ marginTop: '1rem' }}>
            <p><strong>ID:</strong> {currentUser.id}</p>
            <p><strong>Email:</strong> {currentUser.email}</p>
            <p><strong>Role:</strong> {currentUser.role_name ?
              <Tag color="blue">{currentUser.role_name}</Tag> :
              <Tag color="default">No role assigned</Tag>}
            </p>
            <p><strong>Status:</strong> {currentUser.is_verified ?
              <Tag color="success">Verified</Tag> :
              <Tag color="warning">Not Verified</Tag>}
            </p>
            <p><strong>Created:</strong> {currentUser.created_at ? new Date(currentUser.created_at).toLocaleDateString('en-IN', {
              year: 'numeric',
              month: 'long',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            }) : 'N/A'}</p>
            {currentUser.updated_at && (
              <p><strong>Updated:</strong> {new Date(currentUser.updated_at).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'long',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}</p>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>No user details available</p>
          </div>
        )}
      </Modal>

      {/* Edit Role Modal */}
      <Modal
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={500}
        destroyOnClose
        centered
        title={`Assign Role to ${selectedUser?.name}`}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Select Role"
            name="role_id"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select
              placeholder="Choose a role"
              size="large"
              showSearch
              optionFilterProp="children"
            >
              {roles.filter(r => r.enable && r.status).map(role => (
                <Option key={role.id} value={role.id}>
                  {role.role_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: '1.5rem' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleModalClose}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={usersLoading}>
                Assign Role
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersTab;
